import { Injectable } from '@angular/core'
import {GuardResult, RouterStateSnapshot, UrlTree} from '@angular/router'
import {
  CmsActivatedRouteSnapshot,
  CmsService,
  PageContext,
  ProtectedRoutesGuard,
  RouteLoadStrategy,
  RoutingConfigService,
  RoutingService
} from '@spartacus/core'
import { CmsPageGuard, CmsPageGuardService } from '@spartacus/storefront'
import { CookieService } from 'ngx-cookie-service'
import { Observable, of } from 'rxjs'
import { first, switchMap, take, tap } from 'rxjs/operators'
import { EbAuthService } from '../../auth/services/eb-auth.service'

@Injectable({
  providedIn: 'root'
})
export class EbCmsPageGuard extends CmsPageGuard {
  static guardName = 'CmsPageGuard'

  constructor(
    protected routingService: RoutingService,
    protected cmsService: CmsService,
    protected protectedRoutesGuard: ProtectedRoutesGuard,
    protected service: CmsPageGuardService,
    protected cookieService: CookieService,
    protected routingConfig: RoutingConfigService,
    protected authService: EbAuthService
  ) {
    super(routingService, cmsService, protectedRoutesGuard, service, routingConfig)
  }

  /**
   * Tries to load the CMS page data for the anticipated route and returns:
   * - `true` - if it can be activated
   * - `false` - if it cannot be activated
   * - `UrlTree` - if user should be redirected to a given `UrlTree`
   *
   * If the route can be activated, it fires additional calculations on the CMS components present on this CMS page,
   * based on their configuration (`cmsComponents` config).
   *
   * For more, see docs of the `CmsPageGuardService.canActivatePage`.
   */
  canActivate(
    route: CmsActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<GuardResult> {
    return this.protectedRoutesGuard.canActivate(route).pipe(
      switchMap(canActivate =>
        canActivate === true
          ? this.routingService.getNextPageContext().pipe(
              take(1),
              tap(() => {
                if (!this.accessToken && this.authService.isSecuredPage(route.url?.[0]?.path)) {
                  return false
                } else if (
                  !this.accessToken &&
                  !this.authService.isSecuredPage(route.url?.[0]?.path)
                ) {
                  return true
                }
              }),
              switchMap(pageContext =>
                this.cmsService.getPage(pageContext, this.ebShouldReload(pageContext)).pipe(
                  first(),
                  switchMap(pageData =>
                    pageData
                      ? this.service.canActivatePage(pageContext, pageData, route, state)
                      : of(false)
                  )
                )
              )
            )
          : of(canActivate)
      )
    )
  }

  /**
   * Returns whether we should reload the CMS page data, even when it was loaded before.
   */
  private ebShouldReload(pageContext: PageContext): boolean {
    return (
      this.routingConfig.getLoadStrategy() !== RouteLoadStrategy.ONCE &&
      pageContext?.id.indexOf('/search/') === -1
    )
  }

  get accessToken() {
    return (
      this.cookieService.get('access_token') ||
      this.cookieService.get('temp_customer_emulation_token')
    )
  }
}
